/* eslint-disable @typescript-eslint/naming-convention */
import {
  Component,
  ChangeDetectorRef,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { SelectionModel } from '@angular/cdk/collections';
import { noop, Observable, of } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import cloneDeep from 'lodash-es/cloneDeep';

import { AccountService, AuthService } from 'app/core';
import { Account, AccountColor, FieldDef } from 'app/core/models/account.types';
import { PanelsInterfaceComponent, PanelsInterfaceComponentBase } from '../panels-interface.component';
import { Pipeline, Project, ProjectTemplate, Property } from 'app/modules/projects/projects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ResearchService } from 'app/modules/research/research.service';
import { ProjectAddAddressComponent } from 'app/modules/projects/modals/project-add-address/project-add-address.component';
import { ProjectAddChildComponent } from 'app/modules/projects/modals/project-add-child/project-add-child.component';
import { DemographicsDialogComponent } from 'app/shared/modals/demographics/demographics.component';
import { PropertyDealAddComponent } from 'app/modules/properties/modals/deal-add/deal-add.component';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { PipelineService } from 'app/modules/projects/pipeline.service';
import { LayoutService } from 'app/layout/layout.service';
import { RelatedToType } from 'app/shared/enums';
import { setupAddDealByDrawer } from 'app/modules/projects/@shared/functions.utils';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { calendarColors } from 'app/modules/calendar/sidebar/calendar-colors';
import { Select } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { FeatureFlag } from '@core/enums';
import { TableAddressRendererComponent } from '@shared/components/ts-table-renderers/table-address-renderers/table-address-renderer.component';
import { NewPermission } from '@core/enums/permission';
import { AddPropertyModalComponent } from '../../add-property-modal/add-property-modal.component';

@Component({
  selector: 'project-panel-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsMapComponent extends PanelsInterfaceComponentBase implements PanelsInterfaceComponent, OnInit {
  @Input() project: Project;
  @Input() account: any;
  @Input() panelDef: any;
  @Input() data: any;
  @Input() readonly: boolean = false;
  @Input() canEditPanelDef = true;
  @Output() onUpdate: EventEmitter<string> = new EventEmitter();
  @Output() onEditPanelDef: EventEmitter<any> = new EventEmitter();
  @Output() checkboxColumnChanged = new EventEmitter<boolean>();

  @Select(AccountState.selectAccountColorsWithNone) protected readonly accountColors$: Observable<AccountColor[]>;

  titleFieldDef: any;
  panelName: string;
  editMode: boolean;
  changeTitleMode: boolean = false;

  properties: Property[] = [];
  children: Project[] = [];
  displayColumns: string[];
  lat: number = 40;
  lng: number = -74.5;

  loadingParcel: boolean = false;
  layers = [];
  selectedMapStyle: string = 'mapbox://styles/mapbox/streets-v9';
  public googleEarthConfig = {
    // Value above sea level. 100,000 meters is the default value.
    cameraAltitude: 100000,
    zoom: 0,
  };

  isPortfolio: boolean = false;
  portfolioSplitCheckbox: boolean = false;
  selectedProperties = new SelectionModel<Property>(true, []);
  selectedChildProjects = new SelectionModel<Project>(true, []);
  previousSelection: any[] = [];
  splitRunning: boolean = false;
  panelColor: any = null;
  headerColors: any;
  protected readonly NewPermission = NewPermission;

  columnDefs: ColumnDef[] = [];
  rowData: any[] = [];
  actions: TableActions[] = [];
  newTable: boolean;

  private _account: Account;
  private pipeline: Pipeline;
  private fieldDefs: FieldDef[];

  private get projectTemplates(): { id: number; name: string }[] {
    return this.data?.options?.project_templates
      .filter(({ hidden, related_to_type }) => !hidden && related_to_type == RelatedToType.Project)
      .sort((a: ProjectTemplate<any>, b: ProjectTemplate<any>) => a.rank - b.rank);
  }

  constructor(
    private readonly _projectsService: ProjectsService,
    private readonly _matDialog: MatDialog,
    private readonly _router: Router,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _accountService: AccountService,
    private readonly _snackBar: MatSnackBar,
    private readonly _researchService: ResearchService,
    private readonly authService: AuthService,
    private readonly layoutService: LayoutService,
    private readonly pipelineService: PipelineService,
    private readonly foldersTabService: FoldersTabService,
    private readonly confirmService: ConfirmService,
  ) {
    super(_changeDetectorRef);

    this.displayColumns = ['address', 'actions'];
    this._accountService.currentAccount.pipe(untilComponentDestroyed(this)).subscribe((accountData) => {
      this._account = accountData;
    });
    this.headerColors = ['None', ...calendarColors];
  }

  get isAddressHidden(): boolean {
    return this.fieldDefs.find(
      (field) => field.name === 'address_string' && field.related_to_type === RelatedToType.Project,
    )?.meta?.value_hidden;
  }

  ngOnInit() {
    this.fillProperties();
    this.fieldDefs = this.data.fieldDefs;

    this.setupStyle();
    this.titleFieldDef = {
      name: 'panel_name',
      label: this.panelDef.title,
      type: 'text',
    };
    this._projectsService.project$.pipe(untilComponentDestroyed(this)).subscribe((project: Project) => {
      if (this.project) {
        this.project = project;
        this.fillProperties();
      }
      this._changeDetectorRef.markForCheck();
    });

    if (this._account.subdomain == 'mrp') {
      this.layers.push({
        id: '13',
        name: 'Walmarts',
        url: 'https://termsheet-data-lake.s3.amazonaws.com/pvw/walmarts.geojson',
        type: 'circle',
        active: true,
      });
    }

    this._projectsService.pipeline$.pipe(untilComponentDestroyed(this)).subscribe((pipeline: Pipeline) => {
      if (pipeline) {
        this.pipeline = pipeline;
      }
    });
    this.panelColor = this.panelDef?.color;

    this.checkTable();
    if (this.newTable) {
      this.setColDefs();
    }
  }

  setupStyle() {
    const mapStyle = localStorage.getItem('mapStyle');
    if (mapStyle) {
      try {
        this.selectedMapStyle = mapStyle;
      } catch (e) {
        this.selectedMapStyle = 'mapbox://styles/mapbox/satellite-streets-v11';
      }
    } else {
      this.selectedMapStyle = 'mapbox://styles/mapbox/satellite-streets-v11';
    }
  }

  changeMapStyle(style) {
    this.selectedMapStyle = style;
    localStorage.setItem('mapStyle', style);
  }

  openDemographicDialog(property): void {
    this._matDialog.open(DemographicsDialogComponent, {
      width: '50vw',
      data: {
        project: this.project,
        data: this.data,
        property: property,
      },
    });
  }

  update(params) {
    const currentKey = Object.keys(params)[0];
    if (currentKey === 'panel_name') {
      this.panelName = params.panel_name;
    }
  }

  saveLayout(): void {
    if (this.panelName) {
      this.panelDef.title = this.panelName;
    }

    if (this.panelColor) {
      this.panelDef.color = this.panelColor;
    } else {
      this.panelDef.color = null;
    }

    this.onEditPanelDef.emit(this.panelDef);
    this.toggleEditMode(false);
  }

  toggleChangeTitleMode() {
    this.changeTitleMode = true;
  }

  toggleEditMode(editMode: boolean | null = null): void {
    if (editMode === null) {
      this.editMode = !this.editMode;
    } else {
      this.editMode = editMode;
    }
    this.changeTitleMode = false;
    this._changeDetectorRef.markForCheck();
  }

  fillProperties() {
    if (this.project) {
      this.properties = [];
      this.children = [];

      if (this.project.child_ids?.length > 0) this.isPortfolio = true;

      if (this.isPortfolio) {
        this.project?.children?.forEach((child) => {
          if (child.properties?.length) {
            this.properties.push(child.properties[0]);
            this.properties[this.properties.length - 1].project_id = Number(child.id);
            this.properties[this.properties.length - 1].pipeline_id = child.pipeline_id;
          }
          this.children.push(child);
        });
      } else {
        if (this.project.properties && this.project.properties.length > 0) {
          this.properties = [...this.project.properties];
          this.rowData = this.properties;
        }
      }

      if (this.properties?.length) {
        const property = this.properties[0];
        if (property && property.address && property.address.latitude && property.address.longitude) {
          this.lat = property.address.latitude;
          this.lng = property.address.longitude;
        }
      }
    }

    this._changeDetectorRef.markForCheck();
  }

  convertToPortolio(): void {
    if (!this.isPortfolio) {
      this.confirmService
        .openConfirmationModal({
          message:
            'This will convert all existing properties to child deals ' +
            'and allow you track and underwrite each individually. Do you wish to proceed?',
        })
        .pipe(filter((result: boolean) => result))
        .subscribe(() => {
          this._snackBar.open('Deal will now begin conversion. This can take up to a minute.', '', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });

          const project = this.project;

          this._projectsService
            .convertToPortfolio(project.id)
            .pipe(untilComponentDestroyed(this))
            .subscribe(() => {
              this._projectsService
                .getProjectById(project.id)
                .pipe(untilComponentDestroyed(this))
                .subscribe(() => {
                  this._snackBar.open('Deal has successfully been converted to a portfolio.', '', {
                    duration: 3000,
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                  });
                });
            });
        });
    }
  }

  onSaveOnlyLatLong(latitude: string, longitude: string, dialogRef: any) {
    const property = {
      name: `${latitude}, ${longitude}`,
      address: {
        latitude: Number(latitude),
        longitude: Number(longitude),
      },
    };
    this._projectsService
      .addProperty(property as Property)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.loadingParcel = false;
        this._changeDetectorRef.markForCheck();
      });
    dialogRef.close();
  }

  onSaveCompleteAddress(address: string, latitude: string, longitude: string, dialogRef: any) {
    const property = {
      name: address,
      address: {
        address_string: address,
        latitude: Number(latitude),
        longitude: Number(longitude),
      },
    };
    this._projectsService
      .addProperty(property as Property)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.loadingParcel = false;
        this._changeDetectorRef.markForCheck();
      });
    dialogRef.close();
  }

  onSelectParcel($event): void {
    if (!this.canEditDeal()) {
      return;
    }

    const feature = $event.features[0];
    this.loadingParcel = true;
    if ($event.lngLat?.lat && $event.lngLat?.lng) {
      this._researchService
        .reverseGeocode(feature.properties.parcelnumb, $event.lngLat.lat, $event.lngLat.lng)
        .pipe(untilComponentDestroyed(this))
        .subscribe((data) => {
          let _dialogRef = this._matDialog.open(AddPropertyModalComponent, {
            minHeight: '240px',
            minWidth: '600px',
            data: {
              address: data?.address ?? '',
              latitude: $event.lngLat?.lat?.toString?.() ?? '',
              longitude: $event.lngLat?.lng?.toString?.() ?? '',
              city: data?.city ?? '',
              state: data?.state ?? '',
              onCancel: () => {
                _dialogRef.close();
                this.loadingParcel = false;
                this._changeDetectorRef.markForCheck();
              },
              saveOnlyLatLng: () =>
                this.onSaveOnlyLatLong($event.lngLat.lat?.toString?.(), $event.lngLat?.lng?.toString?.(), _dialogRef),
              saveCompleteAddress: () =>
                this.onSaveCompleteAddress(
                  data?.address ?? '',
                  $event.lngLat.lat?.toString?.(),
                  $event.lngLat?.lng?.toString?.(),
                  _dialogRef,
                ),
            },
          });
        });
      return;
    }
    console.error('Unable to select the parcel at the moment');
  }

  onSelectLatLng($event: any): void {
    if (!this.canEditDeal()) {
      return;
    }

    this.loadingParcel = true;

    if ($event.lngLat?.lat && $event.lngLat?.lng) {
      this._researchService
        .reverseGeocode(null, $event.lngLat.lat, $event.lngLat.lng)
        .pipe(untilComponentDestroyed(this))
        .subscribe((data) => {
          let _dialogRef = this._matDialog.open(AddPropertyModalComponent, {
            minHeight: '240px',
            minWidth: '600px',
            data: {
              address: data?.address ?? '',
              latitude: $event.lngLat.lat?.toString?.() ?? '',
              longitude: $event.lngLat?.lng?.toString?.() ?? '',
              city: data?.city ?? '',
              state: data?.state ?? '',
              onCancel: () => {
                _dialogRef.close();
                this.loadingParcel = false;
                this._changeDetectorRef.markForCheck();
              },
              saveOnlyLatLng: () =>
                this.onSaveOnlyLatLong($event.lngLat.lat?.toString?.(), $event.lngLat?.lng?.toString?.(), _dialogRef),
              saveCompleteAddress: () =>
                this.onSaveCompleteAddress(
                  data?.address ?? '',
                  $event.lngLat.lat?.toString?.(),
                  $event.lngLat?.lng?.toString?.(),
                  _dialogRef,
                ),
            },
          });
          this.loadingParcel = false;
        });
      return;
    }
    console.error('Unable to select the parcel in the lat long at the moment');
  }

  openPropertyProjectDialog(): void {
    if (this.isPortfolio) {
      this.openAddChildProjectDialog();
    } else {
      this.openPropertyDialog();
    }
  }

  openPropertyDialog(property?: Property): void {
    const dialogRef = this._matDialog.open(ProjectAddAddressComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { data: this.data, property: property, project: this.project },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.fillProperties();
        this._changeDetectorRef.markForCheck();
      });
  }

  openAddChildProjectDialog(): void {
    const dialogRef = this._matDialog.open(ProjectAddChildComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { data: this.data, project: this.project },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.fillProperties();
        this._changeDetectorRef.markForCheck();
      });
  }

  goToResearch(property): void {
    this._router.navigate(['/research'], {
      queryParams: { lat: property.address.latitude, lng: property.address.longitude },
    });
  }

  deleteProperty(property: Property): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        tap(() => {
          this.loadingParcel = true;
          this.changeDetectorRef.detectChanges();
        }),
        untilComponentDestroyed(this),
        switchMap((res) => {
          if (res) {
            if (this.isPortfolio) {
              return this._projectsService.deleteProject(String(property.id)).pipe(
                untilComponentDestroyed(this),
                switchMap(() => {
                  this._projectsService.bustProjectCache();
                  return this._projectsService.getProjectById(this.project.id);
                }),
              );
            } else {
              return this._projectsService.removePropertyToProjectRelationship(property);
            }
          }

          return of(null);
        }),
      )
      .subscribe(() => {
        this.loadingParcel = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  createDealFromSelectedProperties(dealType: string, updatePastPortfolio?: Project): void {
    const dialogRef = this._matDialog.open(PropertyDealAddComponent, {
      panelClass: 'mailbox-compose-dialog',
      width: '40vw',
      disableClose: true,
      data: { data: this.data, selectedChildProjects: this.selectedChildProjects, dealType, updatePastPortfolio },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.splitRunning = false;
    });
  }

  splitSingleProject(updatePastPortfolio, project): void {
    project.parent_id = null;

    this._projectsService
      .saveProject(updatePastPortfolio)
      .pipe(untilComponentDestroyed(this))
      .subscribe((proj) => {
        this.splitRunning = false;
        this.goToDeal(project.pipeline_id, project.id);
      });
  }

  splitPortfolioCheckbox(): void {
    this.portfolioSplitCheckbox = !this.portfolioSplitCheckbox;
    this.checkboxColumnChanged.emit(this.portfolioSplitCheckbox);
    this.selectedChildProjects.clear();
    this._changeDetectorRef.markForCheck();
  }

  splitPortfolio(): void {
    const pastPortfolio = cloneDeep(this.project);
    const selectedChildIds = this.selectedChildProjects.selected.map((project) => project.id);
    const child_ids = pastPortfolio.child_ids?.filter((id) => !selectedChildIds.includes(id));
    const updatePastPortfolio = { ...{ child_ids: child_ids }, id: pastPortfolio?.id } as Project;

    this.splitRunning = true;

    //[sc-10528]
    if (selectedChildIds?.length == 1) {
      this.splitSingleProject(updatePastPortfolio, this.selectedChildProjects.selected[0]);
    } else {
      this.createDealFromSelectedProperties('splitPortfolio', updatePastPortfolio);
    }
  }

  moveToSingleDeal(property: Property): void {
    this.confirmService
      .openConfirmationModal({
        title: 'Move Deal Outside Portfolio',
        message: 'Are you sure you want to move this deal outside the portfolio?',
      })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        const child_ids = this.project.child_ids?.filter((id) => id !== property?.id);
        const options = { ...{ child_ids: child_ids }, id: this.project?.id } as Project;

        this._projectsService
          .saveProject(options, this.project)
          .pipe(
            untilComponentDestroyed(this),
            finalize(() =>
              this._projectsService.getProjectById(this.project.id).pipe(untilComponentDestroyed(this)).subscribe(),
            ),
          )
          .subscribe(() => {
            this.goToDeal(this.project.pipeline_id, property.id);
          });
      });
  }

  goToDeal(pipelineId: number, projectId: number): void {
    this._router.navigateByUrl(`/projects/${pipelineId}/${projectId}`);
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  openCustomDrawer(): void {
    setupAddDealByDrawer(
      {
        projectService: this._projectsService,
        pipelineService: this.pipelineService,
        foldersTabService: this.foldersTabService,
        layoutService: this.layoutService,
        router: this._router,
        matDialog: this._matDialog,
        authService: this.authService,
      },
      {
        pipeline: this.pipeline,
        parentProject: this.project,
        account: this.account,
        fieldDefs: this.fieldDefs,
        data: this.data,
        projectTemplates: this.projectTemplates,
        disableItemSuggestion: true,
      },
    ).subscribe(noop);
  }

  centerProperty(property: Property | Project): void {
    this.lat = property.address.latitude;
    this.lng = property.address.longitude;
  }

  setColDefs(): void {
    this.columnDefs = [
      {
        label: this.isPortfolio ? 'Portfolio' : 'Address',
        name: 'address',
        type: 'text',
        tsTableOptions: {
          headerName: this.isPortfolio ? 'Portfolio' : 'Address',
          // @ts-ignore
          field: 'title' || 'name' || 'address.address_string',
          autoHeight: true,
          flex: 1,
          cellRenderer: TableAddressRendererComponent,
          cellRendererParams: {
            clickable: true,
            isAddressHidden: this.isAddressHidden,
          },
        },
        meta: {
          readonly: true,
        },
      },
    ];
    this.setTableActions();
  }

  setTableActions(): void {
    if (!this.isAddressHidden) {
      this.actions = [
        {
          label: 'Center Property on Map',
          action: (item) => this.centerProperty(item),
          icon: 'my_location',
        },
        {
          label: 'View Demographics',
          action: (item) => this.openDemographicDialog(item),
          icon: 'supervisor_account',
        },
        {
          label: 'Edit',
          action: (item) => this.openPropertyDialog(item),
          icon: 'edit',
        },
        {
          label: 'Remove',
          action: (item) => this.deleteProperty(item),
          icon: 'delete',
        },
        {
          label: 'View on Google Earth',
          action: (item) => this.openGoogleEarth(item),
          icon: 'public',
        },
        {
          label: 'View on Google Maps',
          action: (item) => this.openGoogleMaps(item),
          icon: 'location_on',
        },
        {
          label: 'View on Parcel Map',
          action: (item) => this.goToResearch(item),
          icon: 'map',
        },
      ];
      if (this.isPortfolio) {
        this.actions.push({
          label: 'Move to Single Deal',
          action: (item) => this.moveToSingleDeal(item),
          icon: 'call_merge',
        });
      }
    }
  }

  private checkTable(): void {
    this.newTable = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_TABLE));
  }

  onSelectionChanged(event: any[]) {
    if (!event.length) {
      this.selectedChildProjects.clear();
      this.previousSelection = [];
      return;
    }
    const selectedDifference = event.filter((item) => !this.previousSelection.includes(item));
    const deselectedDifference = this.previousSelection.filter((item) => !event.includes(item));
    selectedDifference.forEach((item) => {
      this.selectedChildProjects.select(item);
    });
    deselectedDifference.forEach((item) => {
      this.selectedChildProjects.deselect(item);
    });
    this.previousSelection = [...event];
  }

  openGoogleEarth(property: any): void {
    if (property.address?.latitude && property.address?.longitude) {
      const url = `https://earth.google.com/web/@${property.address.latitude},${property.address.longitude},${this.googleEarthConfig.cameraAltitude}d,${this.googleEarthConfig.zoom}z`;
      window.open(url, '_blank');
    }
  }

  openGoogleMaps(property: any): void {
    if (property.address?.latitude && property.address?.longitude) {
      const url = `https://www.google.com/maps?q=loc:${property.address.latitude}+${property.address.longitude}&ll=${property.address.latitude},${property.address.longitude}&t=h`;
      window.open(url, '_blank');
    }
  }
}
